import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowLeftRight, Check, Undo2, Loader2 } from 'lucide-react';
import { useEventListener } from 'ahooks';
import { cn } from '@rocco/utils/cn';
import { BoostButton } from './BoostButton';

interface ImageComparisonCurtainProps {
    /** URL of the original image (before upscaling) */
    originalImageUrl: string;
    /** URL of the upscaled image (after AI upscaling) */
    upscaledImageUrl: string;
    /** Scroll position X for the image */
    scrollPosX: number;
    /** Scroll position Y for the image */
    scrollPosY: number;
    /** Zoom factor for the image */
    zoomFactor: number;
    /** Whether the image uses multiply blend mode */
    multiplied: boolean;
    /** Force the comparison handle to use the dark style for light backgrounds */
    shouldShowBlackHandle?: boolean;
    /** Background colour override for clearCut images (replaces the default #f2f1f0) */
    backgroundColor?: string;
    /** Image filters to apply */
    filters?: string;
    /** Callback when the comparison is closed */
    onClose: () => void;
    /** Whether the comparison is visible */
    visible: boolean;
    /** Countdown seconds for auto-close (null if not counting down) */
    autoCloseCountdown?: number | null;
    /** Callback when slider is interacted with (to cancel auto-close) */
    onSliderInteraction?: () => void;
    /** Text to display on the close button */
    closeButtonText?: string;
    /** Show "Keep New Image" and "Revert to Old Image" buttons instead of "Close" */
    showKeepRevertActions?: boolean;
    /** Called when user clicks "Keep New Image" */
    onKeepNewImage?: () => void;
    /** Called when user clicks "Revert to Old Image" */
    onRevertToOldImage?: () => void;
    /** Whether the original image meets minimum dimension requirements (disables Revert when false) */
    canRevert?: boolean;
    /** Whether a keep/revert action is in progress (to disable buttons) */
    isActionInProgress?: boolean;
    /** Show comet-tail glow trailing the handle while dragging (default true) */
    cometFadeOnSlider?: boolean;
    /** Override the "Before" label text */
    beforeLabel?: string;
    /** Override the "After" label text */
    afterLabel?: string;
    /** Override className for the "Before" label */
    beforeClassName?: string;
    /** Override className for the "After" label */
    afterClassName?: string;
    /** Override className for the action buttons container (Keep/Revert/Close) */
    actionsClassName?: string;
}

/**
 * ImageComparisonCurtain component displays a draggable slider comparing
 * the original image vs the AI-upscaled image.
 * Based on AIImageCurtain but adapted for the page builder context.
 */
export const ImageComparisonCurtain = ({
    originalImageUrl,
    upscaledImageUrl,
    scrollPosX,
    scrollPosY,
    zoomFactor,
    multiplied,
    shouldShowBlackHandle = false,
    backgroundColor = '#f2f1f0',
    filters,
    onClose,
    visible,
    autoCloseCountdown,
    onSliderInteraction,
    closeButtonText = 'Close Upscale Comparison',
    showKeepRevertActions,
    onKeepNewImage,
    onRevertToOldImage,
    canRevert = true,
    isActionInProgress,
    cometFadeOnSlider = true,
    beforeLabel = 'Before',
    afterLabel = 'After',
    beforeClassName,
    afterClassName,
    actionsClassName,
}: ImageComparisonCurtainProps) => {
    const effectiveBg = backgroundColor || '#f2f1f0';

    const [curtainLeft, setCurtainLeft] = useState('0'); // Start at 0% (left) - will animate to 50% (middle) on load
    const [dragging, setDragging] = useState(false);
    const [opacity, setOpacity] = useState(0);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [isAnimating, setIsAnimating] = useState(false);
    const [isExitAnimating, setIsExitAnimating] = useState(false);
    const [exitDirection, setExitDirection] = useState<'left' | 'right' | null>(
        null,
    );
    const [dragDirection, setDragDirection] = useState<'left' | 'right' | null>(
        null,
    );
    const ref = useRef<HTMLDivElement>(null);

    // Update container size and opacity when visible changes
    useEffect(() => {
        if (visible) {
            setOpacity(1);
            // Calculate container size for fixed image rendering
            const container = ref.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                setContainerSize({ width: rect.width, height: rect.height });
            }
            // Animate slider from left (0%) to middle (50%) on initial load
            setIsAnimating(true);
            // Small delay to ensure component is rendered before animation starts
            const timer = setTimeout(() => {
                setCurtainLeft('50');
                // Reset animation flag after transition completes
                setTimeout(() => setIsAnimating(false), 600);
            }, 100);
            return () => clearTimeout(timer);
        } else {
            setOpacity(0);
            setCurtainLeft('0'); // Reset to left position when hidden
            setIsAnimating(false);
        }
    }, [visible]);

    // Update container size on resize
    useEffect(() => {
        if (!visible) return;

        const updateSize = () => {
            const container = ref.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                setContainerSize({ width: rect.width, height: rect.height });
            }
        };

        window.addEventListener('resize', updateSize);
        updateSize(); // Initial size

        return () => {
            window.removeEventListener('resize', updateSize);
        };
    }, [visible]);

    const updateCurtain = (movementX = 0) => {
        const container = ref.current;
        if (!container) return;

        const containerBounding = container.getBoundingClientRect();
        const containerWidth = containerBounding.width;
        if (containerWidth === 0) return;

        let newCurtainOffset =
            (Number(curtainLeft) * containerWidth) / 100 + movementX;
        if (newCurtainOffset < 0) newCurtainOffset = 0;

        if (newCurtainOffset > containerBounding.width)
            newCurtainOffset = containerBounding.width;

        setCurtainLeft(((newCurtainOffset * 100) / containerWidth).toFixed(4));

        // Notify parent that slider was interacted with (to cancel auto-close)
        if (movementX !== 0 && onSliderInteraction) {
            onSliderInteraction();
        }
    };

    const onMouseMove = (e: MouseEvent) => {
        if (!dragging) return;
        if (cometFadeOnSlider && e.movementX !== 0) {
            setDragDirection(e.movementX < 0 ? 'left' : 'right');
        }
        updateCurtain(e.movementX);
    };

    useEventListener(
        'mouseup',
        () => {
            setDragging(false);
            setDragDirection(null);
        },
        { target: document },
    );

    useEventListener('mousemove', onMouseMove, {
        target: document,
    });

    useEffect(() => {
        if (!visible) {
            setCurtainLeft('0');
            setDragging(false);
        }
    }, [visible]);

    /** Animate slider to left end (fully upscaled) then fire the keep callback */
    const handleKeepNewImage = () => {
        if (isActionInProgress || isExitAnimating) return;
        setExitDirection('left');
        setIsExitAnimating(true);
        setIsAnimating(true);
        setCurtainLeft('0');
        setTimeout(() => {
            setIsAnimating(false);
            setIsExitAnimating(false);
            setExitDirection(null);
            onKeepNewImage?.();
        }, 500);
    };

    /** Animate slider to right end (fully original) then fire the revert callback */
    const handleRevertToOldImage = () => {
        if (isActionInProgress || isExitAnimating) return;
        setExitDirection('right');
        setIsExitAnimating(true);
        setIsAnimating(true);
        setCurtainLeft('100');
        setTimeout(() => {
            setIsAnimating(false);
            setIsExitAnimating(false);
            setExitDirection(null);
            onRevertToOldImage?.();
        }, 500);
    };

    const handleClose = () => {
        setOpacity(0);
        setTimeout(() => {
            onClose();
        }, 300); // Match transition duration
    };

    if (!visible && opacity === 0) {
        return null;
    }

    return (
        <div
            ref={ref}
            className="absolute inset-0 z-[100] h-full w-full transition-[opacity,visibility] duration-300"
            data-no-magnifier
            style={{
                backgroundColor: effectiveBg,
                visibility: opacity > 0 ? 'visible' : 'hidden',
                opacity,
                pointerEvents: opacity > 0 ? 'auto' : 'none',
            }}
            onMouseDown={(e) => {
                // Stop propagation to prevent triggering parent sortable drag
                e.stopPropagation();
            }}
            onTouchStart={(e) => {
                // Stop propagation to prevent triggering parent sortable drag
                e.stopPropagation();
            }}
        >
            <style>{`
                @keyframes cometFade {
                    0% { opacity: 0.9; width: 120px; }
                    40% { opacity: 0.7; width: 160px; }
                    100% { opacity: 0; width: 60px; }
                }
            `}</style>
            {/* Upscaled image - background-blend-mode blends image with backgroundColor on same element */}
            <div
                className="absolute inset-0 overflow-hidden bg-cover bg-no-repeat"
                style={{
                    backgroundImage: `url(${upscaledImageUrl})`,
                    backgroundColor: effectiveBg,
                    backgroundBlendMode: multiplied ? 'multiply' : 'normal',
                    backgroundPosition: `${scrollPosX}% ${scrollPosY}%`,
                    backgroundSize:
                        zoomFactor !== 100 ? `${zoomFactor}%` : undefined,
                    filter: filters ? filters : 'none',
                }}
            />
            {/* Original image overlay (revealed from left) */}
            <div className="absolute inset-0">
                <div
                    className={cn(
                        'absolute left-0 top-0 h-full select-none overflow-hidden [&>div]:block [&>div]:bg-cover [&>div]:bg-no-repeat',
                        isAnimating &&
                            !dragging &&
                            'transition-[width] duration-500 ease-out',
                    )}
                    style={{
                        width: `calc(${curtainLeft}% + 1px)`,
                        backgroundColor: effectiveBg,
                    }}
                >
                    <div
                        key={originalImageUrl}
                        style={{
                            backgroundImage: `url(${originalImageUrl})`,
                            backgroundColor: effectiveBg,
                            backgroundBlendMode: multiplied
                                ? 'multiply'
                                : 'normal',
                            backgroundPosition: `${scrollPosX}% ${scrollPosY}%`,
                            backgroundSize:
                                zoomFactor !== 100
                                    ? `${zoomFactor}%`
                                    : undefined,
                            filter: filters ? filters : 'none',
                            width:
                                containerSize.width > 0
                                    ? `${containerSize.width}px`
                                    : '100%',
                            height:
                                containerSize.height > 0
                                    ? `${containerSize.height}px`
                                    : '100%',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                        }}
                    />
                </div>
                {/* Draggable handle */}
                <div
                    className={cn(
                        'absolute top-0 z-10 h-full w-1 cursor-ew-resize',
                        shouldShowBlackHandle
                            ? 'bg-black/60 hover:bg-black/70'
                            : 'bg-white/50 hover:bg-white/70',
                        isAnimating &&
                            !dragging &&
                            'transition-[left] duration-500 ease-out',
                    )}
                    onMouseDown={() => {
                        setDragging(true);
                        setIsAnimating(false); // Stop animation when user starts dragging
                        // Notify parent that slider was interacted with (to cancel auto-close)
                        onSliderInteraction?.();
                    }}
                    style={{
                        left: `calc(${curtainLeft}% - 1px)`,
                    }}
                >
                    {/* Comet tail during exit animation or drag */}
                    {(() => {
                        const dir =
                            isExitAnimating && exitDirection
                                ? exitDirection
                                : cometFadeOnSlider && dragging && dragDirection
                                  ? dragDirection
                                  : null;
                        if (!dir) return null;
                        const isExit = isExitAnimating && !!exitDirection;
                        return (
                            <div
                                className="pointer-events-none absolute top-1/2 -translate-y-1/2"
                                style={{
                                    left: dir === 'left' ? '50%' : undefined,
                                    right: dir === 'right' ? '50%' : undefined,
                                    width: isExit ? '120px' : '80px',
                                    height: '40px',
                                    background:
                                        dir === 'left'
                                            ? 'linear-gradient(to right, rgba(255,255,255,0.85), rgba(255,255,255,0))'
                                            : 'linear-gradient(to left, rgba(255,255,255,0.85), rgba(255,255,255,0))',
                                    borderRadius:
                                        dir === 'left'
                                            ? '0 20px 20px 0'
                                            : '20px 0 0 20px',
                                    animation: isExit
                                        ? 'cometFade 500ms ease-out forwards'
                                        : undefined,
                                    opacity: isExit ? undefined : 0.7,
                                    transition: isExit
                                        ? undefined
                                        : 'opacity 100ms ease-out',
                                }}
                            />
                        );
                    })()}
                    <div
                        className={cn(
                            'absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full shadow-[0_1px_3px_0px_rgba(0,0,0,0.2)] [&_svg]:block',
                            shouldShowBlackHandle
                                ? 'bg-black/80 text-white hover:bg-black/70'
                                : 'bg-white/95 text-black hover:bg-white',
                        )}
                    >
                        <ArrowLeftRight size={20} />
                    </div>
                </div>
                {/* Action buttons */}
                {showKeepRevertActions ? (
                    <div
                        className={cn(
                            'absolute bottom-5 left-1/2 z-[15] flex -translate-x-1/2 gap-8',
                            actionsClassName,
                        )}
                    >
                        <BoostButton
                            tooltipText={
                                !canRevert
                                    ? 'Original image dimension is too small, can not revert'
                                    : null
                            }
                            tooltipPosition="top"
                        >
                            <button
                                type="button"
                                className={cn(
                                    'flex min-w-52 items-center justify-center gap-2 rounded-lg border border-black/10 bg-white/95 px-5 py-2.5 text-sm font-medium text-black shadow-[0px_1.5px_9px_0px_rgba(0,0,0,0.1)] transition-colors duration-200 [&_svg]:block [&_svg]:shrink-0',
                                    canRevert
                                        ? 'cursor-pointer hover:bg-white disabled:cursor-not-allowed disabled:opacity-50'
                                        : 'cursor-not-allowed opacity-50',
                                )}
                                onClick={
                                    canRevert
                                        ? handleRevertToOldImage
                                        : undefined
                                }
                                disabled={
                                    canRevert
                                        ? isActionInProgress || isExitAnimating
                                        : undefined
                                }
                                aria-disabled={!canRevert || undefined}
                            >
                                {isActionInProgress && canRevert ? (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                ) : (
                                    <Undo2 size={14} />
                                )}
                                <span className="whitespace-nowrap">
                                    Revert to Original
                                </span>
                            </button>
                        </BoostButton>
                        <button
                            type="button"
                            className="flex min-w-52 cursor-pointer items-center justify-center gap-2 rounded-lg bg-black px-5 py-2.5 text-sm font-medium text-white shadow-[0px_1.5px_9px_0px_rgba(0,0,0,0.1)] transition-colors duration-200 hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:block [&_svg]:shrink-0"
                            onClick={handleKeepNewImage}
                            disabled={isActionInProgress || isExitAnimating}
                        >
                            {isActionInProgress ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Check size={14} />
                            )}
                            <span className="whitespace-nowrap">
                                {autoCloseCountdown && autoCloseCountdown > 0
                                    ? `Keep New Image (${autoCloseCountdown})`
                                    : 'Keep New Image'}
                            </span>
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        className={cn(
                            'absolute bottom-5 left-1/2 z-[15] flex -translate-x-1/2 cursor-pointer items-center gap-2 rounded-lg border border-black/10 bg-white/95 px-5 py-2.5 text-sm font-medium text-black shadow-[0px_1.5px_9px_0px_rgba(0,0,0,0.1)] transition-colors duration-200 hover:bg-white [&_svg]:block [&_svg]:shrink-0',
                            actionsClassName,
                        )}
                        onClick={handleClose}
                    >
                        <X size={14} />
                        <span className="whitespace-nowrap">
                            {autoCloseCountdown && autoCloseCountdown > 0
                                ? `${closeButtonText} (${autoCloseCountdown})`
                                : closeButtonText}
                        </span>
                    </button>
                )}
            </div>
            {/* Before label */}
            <div
                className={cn(
                    'pointer-events-none absolute left-4 top-4 z-20 select-none rounded bg-black/80 px-3 py-1.5 text-sm font-medium text-white',
                    beforeClassName,
                )}
            >
                {beforeLabel}
            </div>
            {/* After label */}
            <div
                className={cn(
                    'pointer-events-none absolute right-4 top-4 z-20 select-none rounded bg-black/80 px-3 py-1.5 text-sm font-medium text-white',
                    afterClassName,
                )}
            >
                {afterLabel}
            </div>
        </div>
    );
};
